/**
 * Adobe Tracking Hooks
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { AdobeTrackingContext, useAdobeTrackingContext } from 'main/adobeTracking/AdobeTrackingContext';
import AdobeTrackingPlugin from 'main/adobeTracking/AdobeTrackingPlugin';
import { logDebug } from 'main/adobeTracking/utils';

// Re-export context and plugin for convenience
export { AdobeTrackingContext, useAdobeTrackingContext, AdobeTrackingPlugin };

// ============================================================================
// useAdobeTracking - Context consumer hook (alias for useAdobeTrackingContext)
// ============================================================================

/**
 * Hook to access Adobe Tracking plugin from context.
 * Must be used within an AdobeTrackingProvider.
 * @returns AdobeTrackingPlugin instance
 */
export const useAdobeTracking = () => {
	const plugin = useAdobeTrackingContext();
	if (!plugin) {
		throw new Error('useAdobeTracking must be used within AdobeTrackingProvider');
	}
	return plugin;
};

// ============================================================================
// useAdobePageTracking - Automatic page view tracking on route changes
// ============================================================================

/**
 * Hook to automatically track page views on route changes.
 * Must be used inside a Router context and AdobeTrackingProvider.
 */
export const useAdobePageTracking = (): void => {
	const plugin = useAdobeTrackingContext();
	const location = useLocation();
	const lastTrackedPath = useRef<string | null>(null);

	useEffect(() => {
		if (!plugin?.isEnabled) {
			return;
		}

		// Only track if pathname actually changed (ignore query params, hash, etc.)
		if (lastTrackedPath.current === location.pathname) {
			return;
		}

		lastTrackedPath.current = location.pathname;
		void plugin.trackPageView().then(() => {
			if (plugin.isLoaded) {
				logDebug(`Route changed, tracking page view: ${location.pathname}`);
			}
		});
	}, [location.pathname, plugin]);
};

/**
 * Creates a new AdobeTrackingPlugin instance.
 * Use this to create the plugin before passing it to AdobeTrackingProvider.
 */
export const createAdobeTrackingPlugin = (config?: {
	enabled?: boolean;
	scriptUrl?: string;
}): AdobeTrackingPlugin => {
	return new AdobeTrackingPlugin(config);
};
